/**
 * CookieConsent
 * -----------------------------------------------------------------------------
 * Route-aware GDPR-style cookie/tracking consent banner.
 *
 * Behavior
 *   - Renders only when localStorage `maiq_cookie_consent` is unset (null).
 *   - On "Accept": stores 'accepted' and calls window.__maiqInitTracking()
 *     (defined in /public/index.html) which loads GA4 + Microsoft Clarity +
 *     Meta Pixel + PostHog exactly once.
 *   - On "Decline": stores 'declined'. No third-party tracker JS is loaded.
 *   - Returning visitors with a stored choice never see the banner again
 *     (index.html auto-inits trackers for prior accepts on page boot).
 *
 * Theming
 *   - Default = MoveAbroadIQ main brand (deep navy + blue accent) used
 *     everywhere except dedicated landing pages.
 *   - Themed variants are mapped by URL prefix below so the banner inherits
 *     each landing page's existing accent palette and feels native, not
 *     globally injected.
 *
 * Adding a new themed landing
 *   1. Append a `{ match: /^\/your-route/, theme: '<themeKey>' }` entry to
 *      ROUTE_THEME_MAP (top-of-list wins).
 *   2. If the accent needed doesn't exist yet, add it to THEMES.
 *
 * The structure / spacing / button order / copy is identical across themes.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'maiq_cookie_consent';

// Each theme = { bg, body, mention, decline, accentBg, accentHoverBg,
//                accentText, accentShadow, privacyLink }
const THEMES = {
  // Default — main MoveAbroadIQ brand: deep navy card, blue button.
  navyBlue: {
    bg: '#0B1F3A',
    body: 'rgba(255,255,255,0.90)',
    mention: '#93C5FD',                       // light blue accent for inline mentions
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#2563EB',
    accentHoverBg: '#1D4ED8',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(37,99,235,0.55)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // UK Immigration Services — exact page palette: navy (#0B1F3A) + UK gold
  // (#C9A14A). Navy glass panel, white body at 80% opacity, gold accent CTA
  // with navy text, transparent bordered decline, gold underlined links.
  ukImmigration: {
    bg: '#0B1F3A',
    body: 'rgba(255,255,255,0.80)',
    mention: '#C9A14A',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#C9A14A',
    accentHoverBg: '#B38C3C',
    accentText: '#0B1F3A',
    accentShadow: '0 10px 25px -12px rgba(201,161,74,0.60)',
    privacyLink: '#C9A14A',
  },

  // Premium soft gold (UK / Estonia / Greece premium landings)
  gold: {
    bg: '#0B1F3A',
    body: 'rgba(255,255,255,0.90)',
    mention: '#C9A96E',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#C9A96E',
    accentHoverBg: '#B89456',
    accentText: '#0B1F3A',
    accentShadow: '0 10px 25px -12px rgba(201,169,110,0.60)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // Deeper gold (Dubai investment / yatırım / dil okulu)
  goldDeep: {
    bg: '#0B1220',
    body: 'rgba(255,255,255,0.90)',
    mention: '#E0B95B',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#C9A54C',
    accentHoverBg: '#E0B95B',
    accentText: '#0B1220',
    accentShadow: '0 10px 25px -12px rgba(201,165,76,0.60)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // Tropical green (Bali + Ireland — emerald-ish accent)
  tropicalGreen: {
    bg: '#0B1F3A',
    body: 'rgba(255,255,255,0.90)',
    mention: '#4ADE80',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#16A34A',
    accentHoverBg: '#15803D',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(22,163,74,0.55)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // UK flag red (Ingiltere Gayrimenkul)
  ukRed: {
    bg: '#0A1F44',
    body: 'rgba(255,255,255,0.90)',
    mention: '#FCA5A5',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#CF142B',
    accentHoverBg: '#A0101F',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(207,20,43,0.55)',
    privacyLink: 'rgba(255,255,255,0.75)',
  },

  // Teal (Kıbrıs Emlak)
  teal: {
    bg: '#0B1F3A',
    body: 'rgba(255,255,255,0.90)',
    mention: '#5EEAD4',
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#0F766E',
    accentHoverBg: '#115E59',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(15,118,110,0.55)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // Warm amber (Turkey Citizenship + Bali company-setup pages — premium
  // warm-stone hero aesthetic with amber CTAs).
  warmAmber: {
    bg: '#1C1917',                            // stone-900 — warm near-black
    body: 'rgba(255,255,255,0.90)',
    mention: '#FBBF24',                       // amber-400 for inline mentions
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#F59E0B',                      // amber-500 (matches Turkey FAB, Bali eyebrow)
    accentHoverBg: '#D97706',                 // amber-600
    accentText: '#1C1917',                    // deep stone — premium high-contrast read on amber
    accentShadow: '0 10px 25px -12px rgba(245,158,11,0.55)',
    privacyLink: 'rgba(255,255,255,0.72)',
  },

  // Baltic / Nordic platinum (Riga Waterfront — cold cinematic luxury).
  // Deep midnight-navy glass with icy platinum-blue accent. Specifically
  // avoids any saturated brand-blue to preserve the Northern-European
  // architectural calm of the Riga hero.
  baltic: {
    bg: '#0A0F1C',                            // matches Riga page background
    body: 'rgba(255,255,255,0.88)',
    mention: '#B07A86',                       // muted Baltic wine — clearly distinct from white
    decline: {
      border: 'rgba(255,255,255,0.16)',
      hover: 'rgba(176,122,134,0.55)',
      text: 'rgba(255,255,255,0.92)',
    },
    accentBg: '#B07A86',                      // dusty wine button
    accentHoverBg: '#9D6975',                 // slightly deeper hover
    accentText: '#FFFFFF',                    // white text on wine — premium high-contrast
    accentShadow: '0 10px 28px -14px rgba(176,122,134,0.45)',
    privacyLink: 'rgba(255,255,255,0.68)',
  },

  // Japan minimal/red (Japan Company Formation EN + TR — Muji/Apple minimal
  // hero with a single decisive crimson CTA). Keeps the shared dark
  // translucent glass panel; swaps the global blue accent for the page's
  // primary red (#BC002D) with the same darker hover (#A30027) used by
  // .jp-btn--primary on the page itself. Inline mentions + Privacy link use a
  // soft red tint for readability on the dark panel.
  japanRed: {
    bg: '#0B1F3A',                            // keep existing dark translucent panel
    body: 'rgba(255,255,255,0.90)',
    mention: '#FCA5A5',                       // soft red for inline mentions (readable on dark)
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#BC002D',                      // Japan primary red (matches .jp-btn--primary)
    accentHoverBg: '#A30027',                 // darker red hover (matches .jp-btn--primary:hover)
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(188,0,45,0.55)',
    privacyLink: '#FCA5A5',                   // red-tinted Privacy Policy link (not blue/white)
  },

  // Japan Business Manager Visa — charcoal/near-black glass panel with the
  // page's decisive red (#DC2626) CTA. Distinct from `japanRed` (Company
  // Formation pages) so neither affects the other. Decline = transparent w/
  // light border; inline mentions + Privacy link use a readable red tint; the
  // panel carries a subtle red-tinted border + soft card-style shadow.
  japanCharcoal: {
    bg: '#111827',                            // dark charcoal / near-black panel
    panelBorder: '1px solid rgba(220,38,38,0.18)', // subtle red-tinted border
    panelShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 22px 48px -26px rgba(0,0,0,0.55)', // soft card shadow
    body: 'rgba(255,255,255,0.90)',           // white / light-gray text
    mention: '#F87171',                       // red-tinted inline mentions
    decline: { border: 'rgba(255,255,255,0.25)', hover: 'rgba(255,255,255,0.55)', text: 'rgba(255,255,255,0.92)' },
    accentBg: '#DC2626',                      // Japan theme red
    accentHoverBg: '#EF4444',                 // slightly brighter red on hover
    accentText: '#FFFFFF',
    accentShadow: '0 10px 25px -12px rgba(220,38,38,0.55)',
    privacyLink: '#F87171',                   // red-tinted Privacy Policy link
  },

  // Spain Digital Nomad Visa — warm Mediterranean LIGHT banner. Unlike every
  // other theme (dark glass + white text), this one is a premium warm-ivory
  // panel with dark slate text and the page's terracotta-red CTA (#D62828 /
  // hover #B71C1C). Uses the optional panel* overrides below so the shared
  // dark rendering path is untouched for all other routes.
  spainWarm: {
    bg: '#FFF8F0',
    panelBg: 'rgba(255,248,240,0.97)',        // near-solid warm ivory
    panelShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 18px 44px -22px rgba(120,62,34,0.34)',
    panelBorder: '1px solid rgba(214,40,40,0.16)',
    body: '#1F2937',
    mention: '#D62828',
    decline: { border: '#D62828', hover: '#B71C1C', text: '#D62828' },
    accentBg: '#D62828',
    accentHoverBg: '#B71C1C',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 24px -12px rgba(214,40,40,0.45)',
    privacyLink: '#D62828',
  },

  // Portugal Golden Visa — warm Portuguese LIGHT banner (red/gold on ivory).
  portugalWarm: {
    bg: '#FDFBF6',
    panelBg: 'rgba(253,251,246,0.97)',
    panelShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 18px 44px -22px rgba(120,62,34,0.34)',
    panelBorder: '1px solid rgba(193,39,45,0.16)',
    body: '#2A211B',
    mention: '#C1272D',
    decline: { border: '#C1272D', hover: '#9E1F24', text: '#C1272D' },
    accentBg: '#C1272D',
    accentHoverBg: '#9E1F24',
    accentText: '#FFFFFF',
    accentShadow: '0 10px 24px -12px rgba(193,39,45,0.45)',
    privacyLink: '#C1272D',
  },
};

// First match wins. Default fallback = navyBlue (main site brand).
const ROUTE_THEME_MAP = [
  // Spain Digital Nomad Visa — warm Mediterranean LIGHT banner.
  { match: /^\/spain-digital-nomad-visa/i, theme: 'spainWarm' },
  { match: /^\/ispanya-digital-nomad-vizesi/i, theme: 'spainWarm' },
  { match: /^\/portugal-golden-visa/i, theme: 'portugalWarm' },
  { match: /^\/portekiz-golden-vize/i, theme: 'portugalWarm' },

  { match: /^\/dubai-investment/i, theme: 'goldDeep' },
  { match: /^\/dubai-yatirim/i, theme: 'goldDeep' },
  { match: /^\/dubai-dil-okulu/i, theme: 'goldDeep' },

  { match: /^\/uk-immigration-services/i, theme: 'ukImmigration' },

  { match: /^\/uk-company-formation/i, theme: 'gold' },
  { match: /^\/ingiltere-sirket-kurulumu/i, theme: 'gold' },
  { match: /^\/estonia-company-formation/i, theme: 'gold' },
  { match: /^\/hong-kong-company-formation/i, theme: 'japanRed' },
  { match: /^\/yunanistan-golden-visa/i, theme: 'gold' },

  { match: /^\/ingiltere-gayrimenkul/i, theme: 'ukRed' },

  // Japan Company Formation (EN + TR) — minimal/red. Swaps the global blue
  // accent for the page's primary red while keeping the dark translucent panel.
  { match: /^\/japan-company-formation/i, theme: 'japanRed' },
  { match: /^\/japonya-sirket-kurulusu/i, theme: 'japanRed' },

  // Japan Business Manager Visa — charcoal/near-black panel + red CTA.
  { match: /^\/japan-business-manager-visa/i, theme: 'japanCharcoal' },

  // IMPORTANT: route order matters — these MUST come before the generic
  // `/bali-` rule below, otherwise the broader prefix wins and the
  // company-setup pages get a green banner instead of their amber CTA.
  { match: /^\/turkey-citizenship/i, theme: 'warmAmber' },
  { match: /^\/bali-sirket-kurulumu/i, theme: 'warmAmber' },
  { match: /^\/bali-company-setup/i, theme: 'warmAmber' },

  { match: /^\/bali-/i, theme: 'tropicalGreen' },
  { match: /^\/irlanda-dil-okulu/i, theme: 'tropicalGreen' },

  { match: /^\/kibris-emlak/i, theme: 'teal' },

  // Riga Waterfront — Baltic / Nordic platinum (cold cinematic luxury).
  { match: /^\/letonya-riga-waterfront/i, theme: 'baltic' },
  { match: /^\/riga-waterfront/i, theme: 'baltic' },
];

// Authenticated / internal portals where the public consent banner is
// intentionally suppressed (cleaner internal UX). Tracking init logic is
// unchanged — `window.__maiqInitTracking()` still auto-runs for users who
// already accepted on a public page in a prior visit.
const HIDDEN_ROUTE_PREFIXES = [
  /^\/b2b(\/|$)/i,
  /^\/admin(\/|$)/i,
  /^\/dashboard(\/|$)/i,
];

const isHiddenRoute = (pathname) =>
  HIDDEN_ROUTE_PREFIXES.some((rx) => rx.test(pathname || ''));

const resolveTheme = (pathname) => {
  const hit = ROUTE_THEME_MAP.find((r) => r.match.test(pathname || ''));
  return THEMES[hit?.theme] || THEMES.navyBlue;
};

const initTrackers = () => {
  try {
    if (typeof window !== 'undefined' && typeof window.__maiqInitTracking === 'function') {
      window.__maiqInitTracking();
    }
  } catch {
    /* no-op */
  }
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [declineHover, setDeclineHover] = useState(false);
  const [acceptHover, setAcceptHover] = useState(false);
  const location = useLocation();
  const theme = resolveTheme(location?.pathname);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        const t = setTimeout(() => setVisible(true), 350);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, 'accepted'); } catch { /* no-op */ }
    initTrackers();
    setVisible(false);
  };

  const decline = () => {
    try { localStorage.setItem(STORAGE_KEY, 'declined'); } catch { /* no-op */ }
    setVisible(false);
  };

  if (!visible) return null;
  // Suppress on internal/authenticated portals — keeps public compliance
  // intact while keeping dashboards visually clean.
  if (isHiddenRoute(location?.pathname)) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      data-theme={Object.keys(THEMES).find((k) => THEMES[k] === theme) || 'navyBlue'}
      className="fixed inset-x-3 bottom-2.5 sm:inset-x-auto sm:left-4 sm:right-4 sm:bottom-3 z-[9999]"
    >
      <div
        className="
          mx-auto max-w-4xl
          rounded-2xl
          ring-1 ring-white/10
          border border-white/[0.07]
          backdrop-blur-xl
          px-4 py-3 sm:px-5 sm:py-3.5
          flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4
        "
        style={{
          // Softer translucent panel — ~72% navy + 24px backdrop blur gives
          // the glass feel without sacrificing white-text contrast (still
          // > WCAG AA on every hero we tested). A subtle inset highlight on
          // the top edge adds the premium SaaS "frosted" bevel.
          // Light themes (e.g. spainWarm) supply panelBg/panelShadow/panelBorder
          // overrides; all dark themes fall through to the original values.
          backgroundColor: theme.panelBg || `${theme.bg}B8` /* ~72% alpha */,
          boxShadow:
            theme.panelShadow ||
            'inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 60px -30px rgba(0,0,0,0.45)',
          ...(theme.panelBorder ? { border: theme.panelBorder } : {}),
        }}
      >
        <div className="flex-1 min-w-0">
          <p
            className="text-[13px] leading-[1.55]"
            style={{ color: theme.body }}
          >
            We use cookies, analytics tools and advertising technologies,
            including <span className="font-medium" style={{ color: theme.mention }}>Google Analytics</span>,{' '}
            <span className="font-medium" style={{ color: theme.mention }}>Meta Pixel</span> and other
            advertising/analytics cookies, to improve our site, measure performance
            and personalise your experience. You can accept or decline at any time.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <a
            href="/privacy"
            className="inline-flex items-center justify-center text-xs font-medium underline underline-offset-4 px-1.5 py-1.5 transition-colors hover:opacity-100"
            style={{ color: theme.privacyLink }}
            data-testid="cookie-consent-privacy-link"
          >
            Privacy Policy
          </a>
          <button
            type="button"
            onClick={decline}
            onMouseEnter={() => setDeclineHover(true)}
            onMouseLeave={() => setDeclineHover(false)}
            className="inline-flex items-center justify-center rounded-lg text-xs font-semibold px-3.5 py-1.5 transition-colors"
            style={{
              border: `1px solid ${declineHover ? theme.decline.hover : theme.decline.border}`,
              color: theme.decline.text,
              backgroundColor: 'transparent',
            }}
            data-testid="cookie-consent-decline"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            onMouseEnter={() => setAcceptHover(true)}
            onMouseLeave={() => setAcceptHover(false)}
            className="inline-flex items-center justify-center rounded-lg text-xs font-semibold px-3.5 py-1.5 transition-colors"
            style={{
              backgroundColor: acceptHover ? theme.accentHoverBg : theme.accentBg,
              color: theme.accentText,
              boxShadow: theme.accentShadow,
            }}
            data-testid="cookie-consent-accept"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
